import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { SlideOut, SlideOutHeader, Skeleton } from '@aimsos/ui';

import { useNote } from '../../hooks/useNote.js';
import { useNoteComments } from '../../hooks/useNoteComments.js';
import { useSaveNote } from '../../hooks/useSaveNote.js';
import { noteToFormValues } from '../../utils/noteFormValues.js';
import { AccessDeniedState, ErrorState, NotFoundState } from '../states/index.js';
import NoteCommentForm from './NoteCommentForm.jsx';
import NoteComments from './NoteComments.jsx';
import NoteDetails from './NoteDetails.jsx';
import NoteForm from './NoteForm.jsx';

/**
 * Preview of one note in a slide-out (CT-5528): the body, the metadata column,
 * the links and the comment thread — and, for the owner, an edit mode.
 *
 * Everything that is not note-specific is the design system's: `SlideOut` for
 * the overlay, the focus trap, Escape and returning focus to the control that
 * opened it, `SlideOutHeader` for the title row. There is no hand-rolled
 * overlay here.
 *
 * `canEdit` comes from the note, i.e. from the API. This file never looks at a
 * role. A note the user may not read answers 403 and shows access denied — not
 * "not found".
 *
 * Layout: the header and the comment box are pinned; the note (capped) and its
 * thread are the two regions that scroll, so a long thread never pushes the
 * input away.
 *
 * Mounted only while a note is selected, so each opening starts from a clean
 * state (no stale draft of a previous note).
 *
 * @param {object} props
 * @param {string} props.noteId
 * @param {string} props.entityType
 * @param {string} props.entityId
 * @param {() => void} props.onClose
 * @param {() => void} [props.onSaved] — best-effort refresh of the list.
 */
const NotePreviewSlideOut = memo(function NotePreviewSlideOut({ noteId, entityType, entityId, onClose, onSaved }) {
  const { t } = useTranslation('unified-profiles');
  const [editing, setEditing] = useState(false);

  const { note, loading, error, accessDenied, notFound, refetch } = useNote({ noteId });
  const thread = useNoteComments({ noteId });

  const handleSaved = useCallback(() => {
    refetch();
    onSaved?.();
  }, [refetch, onSaved]);
  const { save, saving } = useSaveNote({ entityType, entityId, onSaved: handleSaved });

  const startEditing = useCallback(() => setEditing(true), []);
  const stopEditing = useCallback(() => setEditing(false), []);
  const handleSubmit = useCallback(
    async (payload) => {
      const id = await save({ noteId, ...payload });
      if (id) setEditing(false);
      return id;
    },
    [save, noteId],
  );

  const formValues = useMemo(() => (note ? noteToFormValues(note) : undefined), [note]);

  const renderHeader = useCallback(
    (titleId) => (
      <SlideOutHeader
        titleId={titleId}
        title={note?.title || t('notes.preview.title')}
        subtitle={
          note
            ? t('notes.preview.subtitle', { author: note.author?.name ?? '—' })
            : t('notes.preview.loading')
        }
        status={note ? t(`notes.scope.${note.scope}`) : undefined}
        showEdit={Boolean(note?.canEdit) && !editing}
        onEdit={startEditing}
        showClose
        onClose={onClose}
      />
    ),
    [note, editing, startEditing, onClose, t],
  );

  const body = () => {
    if (loading) {
      return (
        <div data-testid="note-loading" className="flex flex-col gap-3" aria-busy>
          <Skeleton width="50%" height="18px" borderRadius="4px" />
          <Skeleton width="90%" height="14px" borderRadius="4px" />
          <Skeleton width="75%" height="14px" borderRadius="4px" />
        </div>
      );
    }
    if (accessDenied) return <AccessDeniedState onBack={onClose} />;
    if (notFound) return <NotFoundState onBack={onClose} />;
    if (error) return <ErrorState message={error} onRetry={refetch} />;
    if (!note) return null;

    if (editing) {
      return <NoteForm mode="edit" initial={formValues} saving={saving} onSubmit={handleSubmit} onCancel={stopEditing} />;
    }
    return <NoteDetails note={note} />;
  };

  const showThread = Boolean(note) && !editing;

  return (
    <SlideOut
      isOpen
      onClose={onClose}
      renderHeader={renderHeader}
      size="M"
      ariaLabel={note?.title || t('notes.preview.title')}
    >
      <div data-testid="note-preview" data-note-id={noteId} className="flex h-full min-h-0 flex-col gap-4">
        {/* Reading: the note scrolls on its own, capped, so a long body never
            pushes the thread and the input off the panel. Editing: the thread is
            gone, so the form takes the whole panel instead of being squeezed
            into the cap with its expanded editor (which alone wants ~50vh). */}
        <div
          className={[
            'scrollbar-aimsos-auto overflow-y-auto',
            editing ? 'min-h-0 flex-1' : 'max-h-[45vh] shrink-0',
          ].join(' ')}
        >
          {body()}
        </div>

        {showThread ? (
          <>
            <div
              data-testid="note-thread"
              className="scrollbar-aimsos-auto min-h-0 flex-1 overflow-y-auto border-t border-edge pt-4"
            >
              <h3 className="mb-3 text-sm font-semibold text-content">{t('notes.comments.title')}</h3>
              <NoteComments
                comments={thread.comments}
                total={thread.total}
                hasMore={thread.hasMore}
                loading={thread.loading}
                loadingMore={thread.loadingMore}
                error={thread.error}
                onLoadEarlier={thread.loadEarlier}
              />
            </div>

            <div className="shrink-0 border-t border-edge bg-surface pt-4">
              <NoteCommentForm onSubmit={thread.addComment} submitting={thread.submitting} />
            </div>
          </>
        ) : null}
      </div>
    </SlideOut>
  );
});

export default NotePreviewSlideOut;
